/**
 * @param {Object} apiPackage - PackageSimulationDto from the API
 * @param {string} packageId - Unique package identifier used to prefix all generated keys
 * @param {number} [periodMonths] - Optional period filter (kept for future use)
 * @param {Array} [selectedTiers] - Optional array of selected tiers to filter by (e.g., [{ id, name }])
 * @returns {Object|null} Transformed worksheet data or null if invalid input
 */
export function transformPackageToWorksheet(apiPackage, packageId, periodMonths, selectedTiers = []) {
    if (!apiPackage?.tiers) return null;

    const tiersToProcess = selectedTiers.length > 0
        ? apiPackage.tiers.filter((tier) =>
            selectedTiers.some((st) => st.id === (tier.tierId || tier.id))
        )
        : apiPackage.tiers;

    if (tiersToProcess.length === 0) return null;

    const plansByIdentifier = {};
    (apiPackage.availablePlans || []).forEach((plan) => {
        plansByIdentifier[plan.identifier || plan.name] = plan;
    });

    return {
        packageId,
        packageName: apiPackage.packageName || apiPackage.name || 'Package',
        tiers: tiersToProcess.map((tier) => {
            const tierId = tier.tierId || tier.id;

            return {
                tierKey: `${packageId}-${tierId}`,
                tierName: tier.name || `Tier ${tierId}`,
                isOutdatedVersion: tier.isOutdatedVersion ?? false,
                productGroups: (tier.productGroups || []).map((group) => {
                    const groupId = group.groupId || group.id;

                    const isIncluded = group.status
                        ? group.status.toLowerCase() !== 'optional'
                        : (group.productGroupType ?? 1) === 1;

                    return {
                        groupKey: `${packageId}-${groupId}`,
                        productGroupId: groupId,
                        groupName: group.name,
                        isIncluded,
                        productGroupType: isIncluded ? 1 : 0,
                        products: (group.products || []).map((product) => {
                            const productId = product.productId || product.id;

                            // Build plan rows from the prices dictionary
                            const plans = Object.entries(product.prices || {}).map(
                                ([planIdentifier, pricePoint]) => {
                                    const planDef = plansByIdentifier[planIdentifier];

                                    const statusLabel = mapApprovalStatus(pricePoint?.approvalStatus);

                                    return {
                                        id: `${packageId}-${tierId}-${planIdentifier}-${productId}`,
                                        identifier: planIdentifier,
                                        planId: planDef?.planId || planIdentifier,
                                        name: planDef?.name || planIdentifier,
                                        months: extractMonths(planIdentifier),
                                        currentPrice: pricePoint?.currentPrice ?? 0,
                                        newPrice: pricePoint?.newPrice ?? (pricePoint?.currentPrice ?? 0),
                                        adjustmentPercentage: pricePoint?.adjustmentPercentage ?? 0,
                                        approvalStatus: pricePoint?.approvalStatus ?? null,
                                        status: statusLabel,
                                        pricingScheme: pricePoint?.pricingScheme ?? 0,
                                        tieredPrices: pricePoint?.tieredPrices ?? [],
                                        isModified: pricePoint?.isModified ?? false,
                                        revenue: 0,
                                        averageDiscount: 0,
                                    };
                                },
                            );

                            return {
                                id: `${packageId}-${tierId}-${groupId}-${productId}`,
                                productId,
                                name: product.name,
                                productSku: product.articleNumber || productId,
                                productType: product.type,
                                plans: plans.length > 0
                                    ? plans
                                    : [
                                        {
                                            id: `${packageId}-${tierId}-default-${productId}`,
                                            planId: 'default',
                                            name: 'Default',
                                            months: 1,
                                            currentPrice: 0,
                                            newPrice: 0,
                                            adjustmentPercentage: 0,
                                            approvalStatus: null,
                                            status: 'Draft',
                                            pricingScheme: 0,
                                            tieredPrices: [],
                                            isModified: false,
                                            revenue: 0,
                                            averageDiscount: 0,
                                        },
                                    ],
                            };
                        }),
                    };
                }),
            };
        }),
    };
}


// Maps backend ItemApprovalStatus enum (integer) to display string.
// Enum: Draft=0, PendingReview=1, Approved=2, Rejected=3
const APPROVAL_STATUS_LABELS = ['Draft', 'PendingReview', 'Approved', 'Rejected'];

function mapApprovalStatus(value) {
    if (typeof value === 'number' && value >= 0 && value < APPROVAL_STATUS_LABELS.length) return APPROVAL_STATUS_LABELS[value];
    if (typeof value === 'string') return value;
    return 'Draft';
}

function extractMonths(planIdentifier) {
    const wrapped = planIdentifier?.match(/\((\d+)M\)/i);
    if (wrapped) return parseInt(wrapped[1], 10);
    const bare = planIdentifier?.match(/^(\d+)M$/i);
    if (bare) return parseInt(bare[1], 10);
    return 1;
}

export default transformPackageToWorksheet;
